import ResourceManager from "../../components/ResourceManager"
import { useI18n } from "../../hooks"
import { useOptions } from "../../options/hooks/use-options"
import { ProjectViewModel } from "../api/project"
import { useProjectMutations } from "../hooks/use-project-mutations"
import { StringMap } from "../../types/common"

const ProjectCustomersWidget = ({ project }: { project: ProjectViewModel }) => {
  const translations = useTranslations(defaultTranslations)
  const { customerOptions } = useOptions(project.maintainerId)
  const { addCustomers, removeCustomers } = useProjectMutations()

  const handleAddCustomers = (ids: string[]) => {
    const customers = customerOptions
      .filter((customer) => ids.includes(customer.id))
      .map((customer) => ({ name: customer.name, id: customer.id }))
    return addCustomers(project.id, customers)
  }

  const handleRemoveCustomer = (id: string) => {
    const customers = project.customers
      .filter((customer) => customer.id === id)
      .map((customer) => ({ name: customer.name, id: customer.id }))
    return removeCustomers(project.id, customers)
  }

  const sortedCustomerOptions = customerOptions
    .slice()
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
  const sortedCustomers = project.customers
    .slice()
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))

  return (
    <ResourceManager
      title={translations.title}
      resourceOptions={sortedCustomerOptions}
      resources={sortedCustomers}
      canAddResource={project?.canUpdateCustomers || false}
      canRemoveResource={project?.canUpdateCustomers || false}
      onRemoveResource={handleRemoveCustomer}
      onAddResources={handleAddCustomers}
      emptyOptionsMessage={translations.emptyOptionsMessage}
      emptyResourcesMessage={translations.emptyResourcesMessage}
      isMultipleSelection
    />
  )
}

const useTranslations = (defaults = defaultTranslations): Translations => {
  const { translations: t } = useI18n("project")
  const translations = (t?.customersWidget || {}) as StringMap

  const {
    title = defaults.title,
    emptyOptionsMessage = defaults.emptyOptionsMessage,
    emptyResourcesMessage = defaults.emptyResourcesMessage,
  } = translations

  return {
    title,
    emptyOptionsMessage,
    emptyResourcesMessage,
  }
}

const defaultTranslations = {
  title: "Customer",
  emptyOptionsMessage: "No customers in this organisation",
  emptyResourcesMessage: "No customers in this project",
}

type Translations = typeof defaultTranslations

export default ProjectCustomersWidget
